import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useDialog } from "@/components/Dialogs";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/date";
import { BookOpen, Star } from "lucide-react";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";
import { Users, Lock } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import ProfileEditor from "@/components/ProfileEditor";
import ActivityBell, { FriendsButton } from "@/components/ActivityBell";
import { ReportLink, Comments } from "@/components/ContentBits";
import { ListCard } from "@/pages/ListsTab";
import { NoteCard } from "@/pages/NotesTab";
import { PostCard, PostImages } from "@/components/Posts";
import { DoubleTapLike } from "@/pages/FeedTab";
import { ReactionBar, DiaryPoster, dateParts, showAccent, ratingLabel, groupByMonth, computeHighlights, DiaryHeader, MonthRibbon, Highlights, EntryExtras } from "@/components/DiaryPoster";

function Stars({ value }) {
  return (
    <span className="inline-flex gap-0.5" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`h-4 w-4 ${value >= n ? "fill-[#C89B3C] text-[#C89B3C]" : "text-muted-foreground/40"}`} />
      ))}
    </span>
  );
}

function JoinBanner({ user, username }) {
  if (user) return null;
  return (
    <div className="rounded-2xl border border-[#C89B3C]/40 bg-gradient-to-r from-[#1a1218] via-[#2a1620] to-[#1a1218] p-5 text-center sm:flex sm:items-center sm:justify-between sm:text-left" data-testid="join-banner">
      <div>
        <p className="font-serif text-xl font-semibold text-amber-50">Start your own theatre diary</p>
        <p className="text-sm text-amber-100/70">Log every show you see, rate it and keep your memories, then add friends to see what they have been to. Free.</p>
      </div>
      <Link to="/login" className="mt-3 inline-flex rounded-full bg-[#C89B3C] px-6 py-2.5 text-sm font-semibold text-black sm:mt-0">Create your diary</Link>
    </div>
  );
}

export default function PublicDiary() {
  const dialog = useDialog();
  const { username } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("diary");
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState({ about: null, lists: [], notes: [], posts: [] });

  useEffect(() => {
    setData(null); setMissing(false); setTab("diary"); setContent({ about: null, lists: [], notes: [], posts: [] });
    api.get(`/public/diary/${encodeURIComponent(username)}`).then((r) => setData(r.data)).catch(() => setMissing(true));
    api.get(`/public/content/${encodeURIComponent(username)}`).then((r) => setContent(r.data)).catch(() => {});
  }, [username, user?.id]);
  const patchContent = (key) => (u) => setContent((c) => ({ ...c, [key]: c[key].map((x) => (x.id === u.id ? { ...x, ...u } : x)) }));

  const likeContent = (kind, key) => async (it) => {
    try {
      await api.put(`/content/${kind}/${it.id}/reaction`, { type: "heart", on: true });
      patchContent(key)({ id: it.id, mine: [...(it.mine || []), "heart"], reactions: { ...(it.reactions || {}), heart: ((it.reactions || {}).heart || 0) + 1 } });
    } catch { toast.error("Could not save that like. Please try again."); }
  };
  const likeEntry = async (it) => {
    try {
      await api.put(`/diary/${it.id}/reaction`, { type: "heart", on: true });
      patchEntry({ id: it.id, mine: [...(it.mine || []), "heart"], reactions: { ...(it.reactions || {}), heart: ((it.reactions || {}).heart || 0) + 1 } });
    } catch { toast.error("Could not save that like. Please try again."); }
  };
  const dt = (it, onLike, card) => <DoubleTapLike key={it.id} enabled={data.viewer === "friend"} liked={(it.mine || []).includes("heart")} onLike={() => onLike(it)}>{card}</DoubleTapLike>;
  const patchEntry = (u) => setData((d) => ({ ...d, entries: d.entries.map((e) => (e.id === u.id ? { ...e, ...u } : e)) }));
  const reload = () => api.get(`/public/diary/${encodeURIComponent(username)}`).then((r) => setData(r.data)).catch(() => setMissing(true));
  const call = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await reload(); }
    catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };

  if (missing) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        <Seo title="Diary not found" noindex />
        <BookOpen className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-4 font-serif text-3xl font-semibold">This diary isn't available</h1>
        <p className="mt-2 text-muted-foreground">It may be private, or the link may be wrong.</p>
        <Link to="/" className="mt-6 inline-flex rounded-full bg-[#C89B3C] px-6 py-2.5 text-sm font-semibold text-black">Go to My Theatre</Link>
      </div>
    );
  }
  if (!data) return <p className="px-4 py-20 text-center text-muted-foreground">Loading…</p>;

  const st = data.stats;
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      {dialog.node}
      <Seo title={`${data.username}'s theatre diary`} noindex />
      <JoinBanner user={user} username={data.username} />
      {data.viewer === "owner" && (
        <p className="mb-4 rounded-xl border border-[#C89B3C]/30 bg-[#C89B3C]/5 px-4 py-3 text-sm text-foreground/80" data-testid="owner-note">
          This is what your friends see: entries you've shared with friends or publicly. Private entries are never shown.
        </p>
      )}
      <section className="mt-8 rounded-2xl border border-[#C89B3C]/30 bg-gradient-to-br from-[#2a1620] to-[#1a1218] p-5 sm:p-6" data-testid="profile-header">
        <div className="flex items-center gap-4 sm:gap-5">
          <Avatar avatar={content.about?.avatar} url={content.about?.avatar_url} name={data.username} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#C89B3C]">{data.viewer === "friend" ? "Friend" : data.viewer === "owner" ? "You" : "Theatregoer"}</p>
            <h1 className="truncate font-serif text-2xl font-bold text-amber-50 sm:text-3xl" data-testid="profile-title">{data.username}</h1>
            <p className="mt-1 text-sm text-amber-100/70" data-testid="profile-stats">
              <strong className="text-amber-50">{st.shows}</strong> show{st.shows === 1 ? "" : "s"} · <strong className="text-amber-50">{st.theatres}</strong> theatre{st.theatres === 1 ? "" : "s"} · <strong className="text-amber-50">{st.this_year}</strong> this year
            </p>
          </div>
        </div>
        {content.about && (content.about.bio || content.about.fav_show || content.about.first_show) && (
          <div className="mt-4" data-testid="about-card">
            {content.about.bio && <p className="text-base text-amber-50/90" data-testid="about-bio">{content.about.bio}</p>}
            <dl className="mt-2 flex flex-wrap gap-x-8 gap-y-1 text-sm">
              {content.about.fav_show && <div><dt className="inline text-amber-100/60">Favourite show: </dt><dd className="inline font-medium text-amber-50" data-testid="about-fav">{content.about.fav_show}</dd></div>}
              {content.about.first_show && <div><dt className="inline text-amber-100/60">First show: </dt><dd className="inline font-medium text-amber-50" data-testid="about-first">{content.about.first_show}</dd></div>}
            </dl>
            {content.about.hidden && <p className="mt-2 text-xs text-red-300" data-testid="about-hidden">Your profile text has been hidden by us. Please contact us if you think that's a mistake.</p>}
            {data.viewer === "friend" && user && <div className="mt-3"><ReportLink kind="about" id={content.about.id} /></div>}
          </div>
        )}
        {data.viewer === "friend" && <Link to={`/watchlist?chat=${encodeURIComponent(data.username)}`} data-testid="message-friend" className="mt-4 inline-flex rounded-full bg-[#C89B3C] px-5 py-2 text-sm font-semibold text-black">Message {data.username}</Link>}
        {data.viewer === "owner" && !editing && (
          <div className="mt-4 flex items-center justify-end gap-2" data-testid="owner-buttons">
            <button type="button" onClick={() => setEditing(true)} data-testid="edit-profile" className="inline-flex rounded-full border border-[#C89B3C]/60 px-5 py-2 text-sm font-semibold text-[#E6C071] hover:bg-[#C89B3C]/10">Edit profile</button>
            <FriendsButton onClick={() => { try { localStorage.setItem("mytheatre-tab", "friends"); } catch { /* ignore */ } nav("/watchlist"); }} />
            <ActivityBell />
          </div>
        )}
        {data.viewer === "owner" && editing && (
          <ProfileEditor onCancel={() => setEditing(false)} onSaved={(a) => {
            setEditing(false);
            const saved = a && a.id ? a : null;
            setContent((c) => ({ ...c, about: saved }));
            api.get(`/public/content/${encodeURIComponent(username)}`).then((r) => setContent({ ...r.data, about: saved ? { ...(r.data.about || {}), ...saved } : r.data.about })).catch(() => {});
          }} />
        )}
        {data.viewer === "public" && user && (
          <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="request-box">
            {data.request_pending
              ? <span className="text-sm text-[#E6C071]" data-testid="request-pending">Request sent ✓</span>
              : <button type="button" disabled={busy} data-testid="add-friend" onClick={() => call(() => api.post("/friends/request", { username: data.username }), "Request sent")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#C89B3C] px-5 py-2 text-sm font-semibold text-black"><Users className="h-4 w-4" /> Add as friend</button>}
            <span className="text-sm text-amber-100/70">Friends can see more of {data.username}'s page.</span>
          </div>
        )}
      </section>

      <div role="tablist" aria-label="Profile sections" className="mt-5 flex gap-1 border-b border-border" data-testid="profile-tabs">
        {[["diary", "Diary", data.entries.length], ["posts", "Posts", (content.posts || []).length], ["lists", "Lists", content.lists.length], ["notes", "Notes", content.notes.length]].map(([k, label, n]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`ptab-${k}`}
            className={`-mb-px border-b-2 px-4 py-3 text-base font-semibold ${tab === k ? "border-[#C89B3C] text-[hsl(var(--gold))]" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {label} <span className="text-sm font-normal text-muted-foreground">{n}</span>
          </button>
        ))}
      </div>
      {tab === "diary" && (<>
      <div className="mt-6"><DiaryHeader title={`${data.username}'s Theatre Diary`} stats={st} kicker={data.viewer === "friend" ? "Friend's theatre diary" : "Theatre diary"} /></div>
      {data.entries.length > 1 && <div className="mt-4"><Highlights h={computeHighlights(data.entries, new Date().getFullYear())} /></div>}

      {data.entries.length === 0 ? (
        <p className="mt-8 rounded-xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">Nothing shared yet.</p>
      ) : (
        <div className="mt-8 space-y-8">
          {groupByMonth(data.entries).map((g) => (
            <section key={g.key}>
              <MonthRibbon label={g.label} count={g.items.length} />
              <ul className="mt-3 space-y-4">
          {g.items.map((e) => (
            <li key={e.id} className="overflow-hidden rounded-2xl border border-border bg-card" style={{ borderLeft: `4px solid ${showAccent(e.show_name)}` }} data-testid="public-entry">
              <DoubleTapLike enabled={data.viewer === "friend"} liked={(e.mine || []).includes("heart")} onLike={() => likeEntry(e)}>
              <div className="flex">
                <div className="w-[5rem] flex-shrink-0 self-stretch bg-secondary sm:w-[6rem]" style={{ minHeight: "7.5rem" }}>
                  <DiaryPoster src={e.image_url} name={e.show_name} />
                </div>
                <div className="min-w-0 flex-1 p-4">
                  {e.is_custom || !e.show_slug
                    ? <span className="font-serif text-xl font-semibold leading-tight">{e.show_name}</span>
                    : <Link to={`/shows/${e.show_slug}`} className="font-serif text-xl font-semibold leading-tight hover:text-primary">{e.show_name}</Link>}
                  {e.venue && <p className="mt-1 text-sm text-muted-foreground">{e.venue}</p>}
                  {e.rating ? <div className="mt-2 flex items-center gap-2"><Stars value={e.rating} /><span className="text-xs font-medium text-[hsl(var(--gold))]">{ratingLabel(e.rating)}</span></div> : null}
                  <EntryExtras e={e} />
                  <PostImages images={e.images} who={data.viewer === "owner" ? "" : data.username} what="diary entry" />
                  {e.visibility === "friends" && <p className="mt-2 inline-flex items-center gap-1 text-xs text-[hsl(var(--gold))]" data-testid="friends-tag"><Lock className="h-3 w-3" /> Friends only</p>}
                  {data.viewer === "friend" && <ReactionBar entry={e} onChange={patchEntry} />}
                  {data.viewer === "owner" && (
                    <>
                      <ReactionBar entry={e} canReact={false} onChange={patchEntry} />
                      {e.reacted_by?.length > 0 && (
                        <ul className="mt-2 space-y-1 text-xs text-muted-foreground" data-testid="reacted-by">
                          {e.reacted_by.map((r) => (
                            <li key={r.id}>{r.username}: {r.type.replace("_", " ")} <button type="button" className="ml-2 underline hover:text-red-400" onClick={() => call(() => api.delete(`/reactions/${r.id}`), "Reaction removed")}>Remove</button></li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                  {(data.viewer === "friend" || data.viewer === "owner") && (
                    <Comments kind="entry" id={e.id} count={e.comment_count || 0} onCount={(c) => c !== (e.comment_count || 0) && patchEntry({ id: e.id, comment_count: c })} />
                  )}
                </div>
                <div className="flex w-[4.5rem] flex-shrink-0 flex-col items-center justify-center gap-0.5 border-l border-dashed border-[#C89B3C]/40 bg-foreground/5 px-2 text-center sm:w-[5.5rem]">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-[#C89B3C]">{dateParts(e.date).month}</span>
                  <span className="font-serif text-3xl font-bold leading-none text-foreground">{dateParts(e.date).day}</span>
                  <span className="text-xs text-muted-foreground">{dateParts(e.date).year}</span>
                </div>
              </div>
              </DoubleTapLike>
            </li>
          ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      </>)}
      {tab === "posts" && (
        <section className="mt-6" data-testid="profile-posts">
          {(content.posts || []).length === 0
            ? <p className="rounded-xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground" data-testid="no-posts">No posts shared yet.</p>
            : <div className="space-y-5">{content.posts.map((x) => dt(x, likeContent("post", "posts"), <PostCard post={x} mine={data.viewer === "owner"} canReact={data.viewer === "friend"} canReport={!!user && data.viewer !== "owner"} canComment={data.viewer === "friend" || data.viewer === "owner"} onChange={patchContent("posts")} />))}</div>}
        </section>
      )}
      {tab === "lists" && (
        <section className="mt-6" data-testid="profile-lists">
          {content.lists.length === 0 && <p className="rounded-xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground" data-testid="no-lists">No lists shared yet.</p>}
          <div className="mt-4 space-y-5">
            {content.lists.map((l) => dt(l, likeContent("list", "lists"), <ListCard list={l} pinned={content.about?.pinned_list_id === l.id} canReact={data.viewer === "friend"} canReport={!!user && data.viewer !== "owner"} canComment={data.viewer === "friend" || data.viewer === "owner"} onChange={patchContent("lists")} />))}
          </div>
        </section>
      )}
      {tab === "notes" && (
        <section className="mt-6" data-testid="profile-notes">
          {content.notes.length === 0 && <p className="rounded-xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground" data-testid="no-notes">No notes shared yet.</p>}
          <div className="mt-4 space-y-5">
            {content.notes.map((n) => dt(n, likeContent("note", "notes"), <NoteCard note={n} canReact={data.viewer === "friend"} canReport={!!user && data.viewer !== "owner"} canComment={data.viewer === "friend" || data.viewer === "owner"} onChange={patchContent("notes")} />))}
          </div>
        </section>
      )}

      {data.viewer === "friend" && (
        <p className="mt-10 text-center text-xs text-muted-foreground" data-testid="friend-actions">
          <button type="button" className="underline hover:text-foreground" onClick={async () => { if (await dialog.confirm({ title: `Remove ${data.username} as a friend?`, body: "You'll stop seeing each other's friends-only entries, lists and notes.", confirmLabel: "Remove", danger: true })) call(() => api.delete(`/friends/user/${data.owner_id}`), "Friend removed"); }}>Remove friend</button>
          {"  ·  "}
          <button type="button" className="underline hover:text-red-400" onClick={async () => { if (await dialog.confirm({ title: `Block ${data.username}?`, body: "They won't be told, and neither of you will be able to see the other's diary.", confirmLabel: "Block", danger: true })) call(() => api.post("/friends/block", { username: data.username }), "Blocked"); }}>Block</button>
        </p>
      )}
      <div className="mt-10"><JoinBanner user={user} username={data.username} /></div>
      <p className="mt-8 text-center text-sm text-muted-foreground">Curtain up on your next show 🎭 <Link to="/shows" className="text-[#C89B3C] underline">Browse shows</Link></p>
      <p className="mt-4 text-center text-xs text-muted-foreground">
        Something wrong with this page? <a className="underline" href={`mailto:hello@mytheatre.uk?subject=Report diary page: ${encodeURIComponent(data.username)}`}>Report it</a>
      </p>
    </div>
  );
}